import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { SubmitOptionsFlat } from "../api/types"
import { Button } from "../components/ui/button"
import { Card } from "../components/ui/card"
import { Input, Label } from "../components/ui/input"
import { useToast } from "../components/ui/toast"
import { ApiError } from "../lib/api"
import { useCreateRun, useScorers, useSettings } from "../lib/hooks"
import { toCanonicalOptions } from "../lib/options"

const FOCUS_PRESETS = ["auto", "laughter", "discussion", "gaming_highlights", "tension_peak"]
// user 確定 2026-06-11: claude / gemini は未疎通なので候補から除外、default は gpt-5.4-mini。
// 再有効化したい場合は anthropic / gemini を戻して .env に対応キーを設定。
const LLM_MODELS = [
  "openai/gpt-5.4-mini", // default
  "openai/gpt-5.4-nano",
  "openai/gpt-5.4",
  "openai/gpt-5.5",
]
// Phase 5 D-2: PRIMARY_SCORERS は GET /api/settings/scorers から動的取得 (useScorers)。
// fetch 失敗時の fallback として hard-coded リストを保持。
const FALLBACK_PRIMARY_SCORERS = ["llm_scorer", "probabilistic"]

export function Submit() {
  const { data: settings } = useSettings()
  const { data: scorersData } = useScorers()
  const primaryScorers = scorersData?.primary_candidates ?? FALLBACK_PRIMARY_SCORERS
  const create = useCreateRun()
  const nav = useNavigate()
  const toast = useToast()

  const [url, setUrl] = useState("")
  const [focus, setFocus] = useState("gaming_highlights")
  const [customPrompt, setCustomPrompt] = useState("")
  const [llmModel, setLlmModel] = useState(LLM_MODELS[0])
  const [costBudget, setCostBudget] = useState<number>(0.5)
  const [subtitleLang, setSubtitleLang] = useState("auto")
  const [topN, setTopN] = useState<number>(10)
  const [minDur, setMinDur] = useState<number>(15)
  const [maxDur, setMaxDur] = useState<number>(90)
  const [primaryScorer, setPrimaryScorer] = useState("llm_scorer")
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // ユーザが手で変更した field は server defaults で上書きしないためのフラグ。
  const [touched, setTouched] = useState({ focus: false, llmModel: false })

  // settings が後から到着しても初期値を上書きできるよう、useEffect で同期。
  // ただしユーザが既に触っている field はそのまま残す。
  useEffect(() => {
    if (!settings) return
    const dFocus = settings.defaults?.focus_preset
    const dModel = settings.defaults?.llm_model
    if (dFocus && !touched.focus) setFocus(dFocus)
    if (dModel && !touched.llmModel) setLlmModel(dModel)
  }, [settings, touched.focus, touched.llmModel])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const flat: SubmitOptionsFlat = {
      focus,
      llm_model: llmModel,
      cost_budget_usd: costBudget,
      subtitles_lang: subtitleLang,
      top_n: topN,
      min_duration_sec: minDur,
      max_duration_sec: maxDur,
      primary_scorer: primaryScorer,
    }
    if (customPrompt.trim()) flat.custom_prompt = customPrompt.trim()
    try {
      const resp = await create.mutateAsync({ url, options: toCanonicalOptions(flat) })
      toast.show({ title: "Run started", description: resp.run_id, variant: "success" })
      nav(`/runs/${resp.run_id}`)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "実行に失敗しました"
      setError(msg)
      toast.show({ title: "実行できませんでした", description: msg, variant: "error" })
    }
  }

  return (
    <div className="flex flex-col items-center gap-8 pb-20 pt-16 px-8">
      <div className="text-center">
        <h1 className="text-[34px] font-semibold leading-[42px] text-text-primary">
          ショート切り抜き候補を抽出
        </h1>
        <p className="mt-3 text-base text-text-secondary">
          配信 URL から「ここが盛り上がる」を自動で候補化します
        </p>
      </div>
      <Card className="w-full max-w-[880px] p-8">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="url">配信 URL</Label>
            <Input
              id="url"
              type="url"
              required
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          <div className="flex gap-4">
            <div className="flex w-60 flex-col gap-1.5">
              <Label htmlFor="focus">Focus プリセット</Label>
              <select
                id="focus"
                value={focus}
                onChange={(e) => {
                  setFocus(e.target.value)
                  setTouched((t) => ({ ...t, focus: true }))
                }}
                className="w-full rounded-lg border border-border-default bg-surface-2 px-3.5 py-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {FOCUS_PRESETS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="custom">カスタムプロンプト (任意)</Label>
              <Input
                id="custom"
                placeholder="例: ツッコミが鋭い箇所中心"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((s) => !s)}
            className="flex items-center gap-3 pt-1 text-left hover:text-text-secondary"
          >
            <span className="text-xs font-medium text-text-muted">
              {showAdvanced ? "▾" : "▸"} 詳細設定
              {!showAdvanced ? " (クリックで展開)" : ""}
            </span>
            <div className="h-px flex-1 bg-border-subtle" />
          </button>

          {showAdvanced ? (
            <>
              <div className="flex gap-3.5">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="model">スコア判定モデル</Label>
                  <select
                    id="model"
                    value={llmModel}
                    onChange={(e) => {
                      setLlmModel(e.target.value)
                      setTouched((t) => ({ ...t, llmModel: true }))
                    }}
                    className="w-full rounded-lg border border-border-default bg-surface-2 px-3.5 py-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {LLM_MODELS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="budget">コスト上限 (USD)</Label>
                  <Input
                    id="budget"
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={costBudget}
                    onChange={(e) => setCostBudget(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex gap-3.5">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="lang">字幕言語</Label>
                  <Input
                    id="lang"
                    value={subtitleLang}
                    onChange={(e) => setSubtitleLang(e.target.value)}
                    placeholder="auto / ja / en"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="topn">Top-N</Label>
                  <Input
                    id="topn"
                    type="number"
                    min={1}
                    max={50}
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex gap-3.5">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="mindur">最短 (秒)</Label>
                  <Input
                    id="mindur"
                    type="number"
                    min={5}
                    max={120}
                    value={minDur}
                    onChange={(e) => setMinDur(Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="maxdur">最長 (秒)</Label>
                  <Input
                    id="maxdur"
                    type="number"
                    min={10}
                    max={180}
                    value={maxDur}
                    onChange={(e) => setMaxDur(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex gap-3.5">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="primary">Primary Scorer</Label>
                  <select
                    id="primary"
                    value={primaryScorer}
                    onChange={(e) => setPrimaryScorer(e.target.value)}
                    className="w-full rounded-lg border border-border-default bg-surface-2 px-3.5 py-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {primaryScorers.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label>並走 Scorer (Phase 2+)</Label>
                  <div className="flex items-center rounded-lg border border-border-default bg-surface-2 px-3.5 py-3 text-sm text-text-muted">
                    —
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {error ? <p className="text-sm text-danger">{error}</p> : null}

          <div className="flex justify-end pt-1">
            <Button type="submit" size="lg" disabled={create.isPending}>
              {create.isPending ? "実行中…" : "抽出を実行 →"}
            </Button>
          </div>
        </form>
      </Card>
      <p className="text-xs text-text-muted">
        <span className="text-info">ⓘ</span> コスト上限を超える見積もりの場合は実行前に停止します。
      </p>
    </div>
  )
}
